import React from "react";
import { Link } from "react-router-dom";
import {
  Search,
  Users,
  Calendar,
  Phone,
  Mail,
  Eye,
  Edit,
  Plus,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { usePatientList, type Patient } from "../../hooks/usePatientList";
import { useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../services/axiosInstance";
import { API_ENDPOINTS } from "../../services/api/apiConfig";

const PatientList: React.FC = () => {
  const queryClient = useQueryClient();
  const {
    patients,
    loading,
    totalPages,
    totalElements,
    filters,
    setFilters: handleFilterChange,
  } = usePatientList();

  const prefetchPatientDetails = (id: number) => {
    // Prefetch using the same keys and functions as usePatientHistory
    // Note: isAccountContext is false for this context
    const idNum = Number(id);

    queryClient.prefetchQuery({
      queryKey: ['patient', 'consultations', String(id), false],
      queryFn: async () => {
        const response = await axiosInstance.get(API_ENDPOINTS.PATIENTS.GET_BY_ID(idNum)).catch(async () => {
          return axiosInstance.get(API_ENDPOINTS.PATIENTS.MALADE(idNum));
        });
        return Array.isArray(response.data) ? response.data : [response.data].filter(Boolean);
      }
    });

    queryClient.prefetchQuery({
      queryKey: ['patient', 'constantes', String(id), false],
      queryFn: async () => {
        const response = await axiosInstance.get(API_ENDPOINTS.CONSTANTES.BY_PATIENT(idNum)).catch(() => ({ data: [] }));
        return Array.isArray(response.data) ? response.data : [];
      }
    });

    queryClient.prefetchQuery({
      queryKey: ['patient', 'medical-records', String(id), false],
      queryFn: async () => {
        const response = await axiosInstance.get(API_ENDPOINTS.MEDICAL_RECORDS.PATIENT(idNum)).catch(() => ({ data: [] }));
        return Array.isArray(response.data) ? response.data : [];
      }
    });
  };


  const getConsultationStatus = (patient: Patient) => {
    if (patient.hasConsultation) {
      return {
        icon: <CheckCircle className="w-4 h-4" />,
        text: 'Consulté',
        color: 'text-green-600 bg-green-50'
      };
    } else {
      return {
        icon: <XCircle className="w-4 h-4" />,
        text: 'Non consulté',
        color: 'text-red-600 bg-red-50'
      };
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestion des Patients</h1>
          <p className="text-slate-600 mt-1">
            {totalElements} patient{totalElements > 1 ? 's' : ''} trouvé{totalElements > 1 ? 's' : ''}
          </p>
        </div>
        {/* <Link
          to="/admin/dashboard/register"
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Patient
        </Link> */}
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Recherche */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Recherche
            </label>
            <div className="relative">
              <Search className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Nom, prénom, téléphone, email..."
                value={filters.searchQuery}
                onChange={(e) => handleFilterChange({ searchQuery: e.target.value })}
                className="pl-10 pr-4 py-2 w-full border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Statut de consultation */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Statut Consultation
            </label>
            <select
              value={filters.consultationStatus}
              onChange={(e) => handleFilterChange({ consultationStatus: e.target.value as any })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Tous les patients</option>
              <option value="consulted">Patients consultés</option>
              <option value="not-consulted">Patients non consultés</option>
            </select>
          </div>

          {/* Tri */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Trier par
            </label>
            <select
              value={`${filters.sortBy},${filters.sortDirection}`}
              onChange={(e) => {
                const [sortBy, sortDirection] = e.target.value.split(',');
                handleFilterChange({ sortBy, sortDirection: sortDirection as any });
              }}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="lastName,asc">Nom (A-Z)</option>
              <option value="lastName,desc">Nom (Z-A)</option>
              <option value="createdAt,desc">Date de création (récent)</option>
              <option value="createdAt,asc">Date de création (ancien)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tableau des patients */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : patients.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Patient
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Date création
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {patients.map((patient: Patient) => {
                    const status = getConsultationStatus(patient);
                    return (
                      <tr key={patient.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <Users className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-slate-900">
                                {patient.firstName} {patient.lastName}
                              </div>
                              {patient.unpaidAmount && patient.unpaidAmount > 0 && (
                                <div className="text-xs text-red-600">
                                  Impayé: {patient.unpaidAmount.toFixed(2)} €
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-900 flex items-center">
                            <Phone className="w-4 h-4 mr-1 text-slate-400" />
                            {patient.phone}
                          </div>
                          <div className="text-sm text-slate-500 flex items-center">
                            <Mail className="w-4 h-4 mr-1 text-slate-400" />
                            {patient.email}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                            {status.icon}
                            <span className="ml-1">{status.text}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                          {formatDate(patient.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <Link
                              to={`/admin/dashboard/patient/view/${patient.id}`}
                              onMouseEnter={() => prefetchPatientDetails(patient.id)}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                              title="Voir les détails"
                            >
                              <Eye className="w-4 h-4" />
                            </Link>
                            <Link
                              to={`/admin/dashboard/patient/edit/${patient.id}`}
                              className="text-slate-600 hover:text-slate-900 p-1 rounded hover:bg-slate-50"
                              title="Modifier"
                            >
                              <Edit className="w-4 h-4" />
                            </Link>
                            <Link
                              to={`/admin/dashboard/record/new/${patient.id}`}
                              className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                              title="Nouveau dossier médical"
                            >
                              <Calendar className="w-4 h-4" />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-slate-50 px-6 py-3 flex items-center justify-between border-t border-slate-200">
                <div className="text-sm text-slate-700">
                  Affichage de {filters.page * filters.size + 1} à{' '}
                  {Math.min((filters.page + 1) * filters.size, totalElements)} sur{' '}
                  {totalElements} résultats
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleFilterChange({ page: Math.max(0, filters.page - 1) })}
                    disabled={filters.page === 0}
                    className="p-2 rounded-lg border border-slate-300 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="px-3 py-1 text-sm text-slate-700">
                    Page {filters.page + 1} sur {totalPages}
                  </span>
                  <button
                    onClick={() => handleFilterChange({ page: Math.min(totalPages - 1, filters.page + 1) })}
                    disabled={filters.page === totalPages - 1}
                    className="p-2 rounded-lg border border-slate-300 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              Aucun patient trouvé
            </h3>
            <p className="text-slate-500 mb-4">
              Essayez de modifier vos critères de recherche
            </p>
            <Link
              to="/admin/dashboard/register"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un patient
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientList;
